import { getLocalStorage, setLocalStorage } from "../../../helper/localStorage";
import type { FeedbackNudgeState } from "./feedbackNudgeLogic";

/**
 * Local record of which feedback form this browser last submitted, and when.
 *
 * One key holds every form. A cache, never a source of truth: for signed-in
 * users the table is authoritative and this only short-circuits the query; for
 * anonymous visitors it is a courtesy, not enforcement.
 */
export const FEEDBACK_STORE_KEY = "mindmap.feedback.v1";

/**
 * How long a submission suppresses the button locally. Bounded rather than
 * permanent so a shared browser is not silenced for every later visitor;
 * signed-in users are suppressed for good by the server check instead.
 */
export const FEEDBACK_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export type FeedbackStore = {
  /**
   * Whose state this is; null for anonymous visitors. Stamped because
   * localStorage is per-browser and a session can end without a clean
   * sign-out; the reader discards anything that is not the current user's.
   */
  userId: string | null;
  /** `key` of the most recently submitted form, or null. */
  lastFormKey: string | null;
  /** When that submission landed, as epoch ms, or null if unknown. */
  lastSubmittedAt: number | null;
  /** The nudge record. Carried through raw; `readNudgeState` sanitises it. */
  nudge?: FeedbackNudgeState;
};

export const readFeedbackStore = (
  currentUserId: string | null,
): FeedbackStore => {
  const empty: FeedbackStore = {
    userId: currentUserId,
    lastFormKey: null,
    lastSubmittedAt: null,
  };

  try {
    const raw = getLocalStorage(FEEDBACK_STORE_KEY);
    if (!raw) {
      return empty;
    }

    const parsed = JSON.parse(raw) as Partial<FeedbackStore> | null;
    if (!parsed || typeof parsed !== "object") {
      return empty;
    }

    // Belongs to somebody else (or to the signed-out version of this browser)
    // → treat as absent. `null === null` makes the anonymous case fall out
    // without a special branch.
    if ((parsed.userId ?? null) !== currentUserId) {
      return empty;
    }

    return {
      userId: currentUserId,
      lastFormKey:
        typeof parsed.lastFormKey === "string" ? parsed.lastFormKey : null,
      lastSubmittedAt:
        typeof parsed.lastSubmittedAt === "number" &&
        Number.isFinite(parsed.lastSubmittedAt)
          ? parsed.lastSubmittedAt
          : null,
      nudge: parsed.nudge,
    };
  } catch {
    // A corrupt or hand-edited value must never break the app.
    return empty;
  }
};

/**
 * Is `formKey` still inside its local cooldown? A record with no timestamp
 * counts as expired, so a stale record fails open.
 */
export const isWithinCooldown = (
  store: FeedbackStore,
  formKey: string,
  now: number = Date.now(),
): boolean =>
  store.lastFormKey === formKey &&
  store.lastSubmittedAt !== null &&
  now - store.lastSubmittedAt < FEEDBACK_COOLDOWN_MS;

/**
 * Write the blob back, preserving whatever this update does not touch. Both
 * halves — the submitted-form cache and the nudge record — share one key, so
 * every writer must merge rather than replace.
 */
export const patchFeedbackStore = (
  currentUserId: string | null,
  patch: Partial<Omit<FeedbackStore, "userId">>,
) => {
  try {
    const next: FeedbackStore = {
      ...readFeedbackStore(currentUserId),
      ...patch,
      userId: currentUserId,
    };
    setLocalStorage(FEEDBACK_STORE_KEY, JSON.stringify(next));
  } catch {
    // setItem throws in Safari private mode and on quota exhaustion. Failing
    // to cache must not fail a submission that already succeeded.
  }
};

export const cacheLastFormKey = (
  currentUserId: string | null,
  formKey: string,
) =>
  patchFeedbackStore(currentUserId, {
    lastFormKey: formKey,
    lastSubmittedAt: Date.now(),
  });
